/**
 * Subscription model — pure, browser-safe helpers shared by the API
 * (subscribe / change-plan / cancel handlers + the webhook) and the SPA
 * (billing switcher + the access gate's lock derivation). No AWS SDK, no
 * Stripe SDK, no node imports — the Stripe rail itself lives in
 * `@notamen/core/payments` (server-only).
 *
 * Design rule (mirrors `compStatus` in @notamen/core/workspace): every reader
 * here is DEFENSIVE and NEVER throws — a missing/garbage subscription
 * summary must resolve to "no subscription, not locked", because the
 * access gate keys off it and the app has NO paywall for never-subscribed
 * workspaces. Getting the safe default wrong would lock free tenants.
 */

import { PLAN_IDS, PLANS, type PlanId } from "./plans.js";

/** Billing cadence for a subscription price. */
export type Cadence = "monthly" | "annual";

export const CADENCES = ["monthly", "annual"] as const;

/**
 * The small subscription summary DENORMALIZED onto the workspace META row
 * by the Stripe webhook, so the per-request access gate reads ONE row
 * (getWorkspace) instead of a second billing read on the hot path.
 *
 * `status` is the raw Stripe subscription status string. `scheduledPlan` /
 * `scheduledCadence` describe a pending downgrade that takes effect at
 * `currentPeriodEnd` (display only — no enforcement hangs off it).
 */
export interface SubscriptionSummary {
  subscriptionId: string;
  plan: PlanId;
  cadence: Cadence;
  /** Raw Stripe status: active | trialing | past_due | canceled | unpaid | incomplete | incomplete_expired */
  status: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  scheduledPlan?: PlanId;
  scheduledCadence?: Cadence;
  updatedAt: string;
}

export interface SubscriptionState {
  /** A subscription summary exists on the workspace at all. */
  has: boolean;
  /** Currently grants paid access (active / trialing / past_due grace). */
  active: boolean;
  /**
   * Access should be WALLED. TRUE only for a terminally-canceled
   * subscription (`status === "canceled"`, i.e. a cancel_at_period_end that
   * has reached period end, or subscription.deleted). NEVER true for
   * never-subscribed (undefined summary), past_due/unpaid/incomplete
   * (no dunning invented), or incomplete_expired (a first subscribe that
   * never succeeded — the workspace never had paid access, so it's treated
   * like never-subscribed, not locked).
   */
  locked: boolean;
  plan: PlanId | null;
  cadence: Cadence | null;
  status: string | null;
}

const NO_SUBSCRIPTION: SubscriptionState = {
  has: false,
  active: false,
  locked: false,
  plan: null,
  cadence: null,
  status: null,
};

function isPlanId(v: unknown): v is PlanId {
  return typeof v === "string" && (PLAN_IDS as readonly string[]).includes(v);
}

function isCadence(v: unknown): v is Cadence {
  return v === "monthly" || v === "annual";
}

/**
 * Derive access/billing state from a (possibly absent / raw) subscription
 * summary. Defensive: an undefined, malformed, or unrecognized summary
 * yields `NO_SUBSCRIPTION` (has:false, locked:false) — the safe default
 * that never paywalls a workspace that didn't complete a subscription.
 */
export function subscriptionStatus(
  summary: SubscriptionSummary | null | undefined,
): SubscriptionState {
  if (!summary || typeof summary !== "object") return NO_SUBSCRIPTION;
  if (typeof summary.subscriptionId !== "string" || !summary.subscriptionId) {
    return NO_SUBSCRIPTION;
  }
  const status = typeof summary.status === "string" ? summary.status : "";
  if (!status) return NO_SUBSCRIPTION;

  // Only a truly-ended subscription locks. Everything else (including
  // past_due / unpaid / incomplete) leaves access open — dunning is a
  // separate feature we deliberately don't invent here.
  const locked = status === "canceled";
  // A live, access-granting subscription. incomplete/incomplete_expired
  // (first charge never cleared) and canceled do NOT count as active.
  const active =
    status === "active" || status === "trialing" || status === "past_due";

  return {
    has: true,
    active,
    locked,
    plan: isPlanId(summary.plan) ? summary.plan : null,
    cadence: isCadence(summary.cadence) ? summary.cadence : null,
    status,
  };
}

/** Rank of a plan in the upgrade ladder (solo < studio < agency < enterprise). */
export function planRank(plan: PlanId): number {
  return (PLAN_IDS as readonly string[]).indexOf(plan);
}

/**
 * Monthly-recurring USD a (plan, cadence) LISTS at — annual amortized ÷12, 0
 * for a custom-priced (null) plan. Status-independent: this is the pricing
 * source of truth for MRR, used both for live paying subs (via
 * `subscriptionMrrUsd`) and for churned/lost-MRR figures where the subscription
 * is already canceled.
 */
export function planMrrUsd(plan: PlanId, cadence: Cadence): number {
  const p = PLANS[plan];
  if (cadence === "annual") {
    return p.priceAnnual != null ? p.priceAnnual / 12 : 0;
  }
  return p.priceMonthly ?? 0;
}

/**
 * Monthly-recurring revenue (USD) a subscription summary contributes RIGHT
 * NOW. Pure + defensive — mirrors `subscriptionStatus`.
 *
 * Returns 0 whenever no money is actually flowing:
 *  - inactive / canceled / incomplete (no live billing),
 *  - **trialing** — counts as `active` for ACCESS, but bills nothing until it
 *    converts, so it contributes $0 to MRR (revenue, not access, is the
 *    question here),
 *  - a custom-priced plan (enterprise has no list price).
 *
 * Only `active` and `past_due` (still owed) are treated as paying.
 */
export function subscriptionMrrUsd(
  summary: SubscriptionSummary | null | undefined,
): number {
  const st = subscriptionStatus(summary);
  if (st.status !== "active" && st.status !== "past_due") return 0;
  if (!st.plan || !st.cadence) return 0;
  return planMrrUsd(st.plan, st.cadence);
}

export type PlanChangeKind = "upgrade" | "downgrade" | "noop";

/**
 * Classify a plan/cadence change as an upgrade (apply immediately, prorated
 * charge now) or a downgrade (apply at period end). A higher plan rank is an
 * upgrade; a lower rank is a downgrade. Same plan: monthly→annual is an
 * upgrade (more commitment, charged now), annual→monthly is a downgrade
 * (apply at period end). Identical plan+cadence is a no-op.
 */
export function classifyPlanChange(
  from: { plan: PlanId; cadence: Cadence },
  to: { plan: PlanId; cadence: Cadence },
): PlanChangeKind {
  const fromRank = planRank(from.plan);
  const toRank = planRank(to.plan);
  if (toRank > fromRank) return "upgrade";
  if (toRank < fromRank) return "downgrade";
  // Same plan — decide on cadence.
  if (from.cadence === to.cadence) return "noop";
  return to.cadence === "annual" ? "upgrade" : "downgrade";
}

export interface PlanLimitCheck {
  ok: boolean;
  /** Human-readable list of the limits exceeded (empty when ok). */
  over: string[];
}

/**
 * Whether the current workspace counts exceed a target plan's hard limits.
 * Used at request time to BLOCK a downgrade that would leave the workspace
 * over the target tier (the owner must reduce first). Only the per-workspace
 * limits (seats, brands) are checked here — the cross-workspace `workspaces`
 * cap is a workspace-creation concern, not a single-workspace downgrade gate.
 * A `null` limit means "unlimited" and never blocks.
 */
export function exceedsPlanLimits(
  plan: PlanId,
  counts: { seats: number; brands: number },
): PlanLimitCheck {
  const p = PLANS[plan];
  const over: string[] = [];
  if (p.seats != null && counts.seats > p.seats) {
    over.push(`${counts.seats} members but ${PLANS[plan].name} allows ${p.seats}`);
  }
  if (p.brands != null && counts.brands > p.brands) {
    over.push(`${counts.brands} brands but ${PLANS[plan].name} allows ${p.brands}`);
  }
  return { ok: over.length === 0, over };
}

/**
 * The committed (sandbox) Stripe Price ID for a plan + cadence, or
 * undefined when the plan has no self-serve price (Enterprise). This is the
 * PURE fallback that reads `PLANS`; the API wraps it in a server-side
 * resolver that prefers an env override for live mode.
 */
export function planPriceId(plan: PlanId, cadence: Cadence): string | undefined {
  const p = PLANS[plan];
  return cadence === "annual" ? p.stripePriceIdAnnual : p.stripePriceIdMonthly;
}

/**
 * Reverse-map a Stripe Price ID to its plan + cadence using the committed
 * `PLANS` ids. This is a FALLBACK only: the webhook resolves plan + cadence
 * from the Stripe price's own `metadata` (plan_id / cadence) first, and only
 * uses this if metadata is absent. Returns null when no plan matches.
 */
export function planFromPriceId(
  priceId: string | null | undefined,
): { plan: PlanId; cadence: Cadence } | null {
  if (!priceId || typeof priceId !== "string") return null;
  for (const plan of PLAN_IDS) {
    const p = PLANS[plan];
    if (p.stripePriceIdMonthly === priceId) return { plan, cadence: "monthly" };
    if (p.stripePriceIdAnnual === priceId) return { plan, cadence: "annual" };
  }
  return null;
}
